import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, Card, CardContent, CardHeader, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tabs, Tab, Alert, CircularProgress, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, Select, MenuItem, 
  FormControl, InputLabel, TextField, Fab, IconButton, Tooltip
} from '@mui/material';
import {
  Assessment, Description, Schedule, Download, Add, 
  Search, Visibility, TrendingUp, BarChart, Dashboard as DashboardIcon
} from '@mui/icons-material';
import api from '../services/api';
import { LoggerService } from '../services/LoggerService';
import { useSnackbar } from 'notistack';

// Interfaces for Reports data
interface ReportTemplate {
  id: string;
  name: string;
  description: string;
  report_type: string;
  category: string;
  template_config: any;
  is_active: boolean;
}

interface Report {
  id: string;
  template_id: string;
  fund_id?: string;
  user_id: string;
  title: string;
  description: string;
  report_type: string;
  status: string;
  generation_method: string;
  report_data: any;
  file_path?: string;
  file_format?: string;
  file_size?: number;
  generation_started_at?: string;
  generation_completed_at?: string;
  scheduled_at?: string;
  parameters: any;
  is_public: boolean;
  created_at: string;
  updated_at: string;
  template?: ReportTemplate;
  fund?: {
    id: string;
    name: string;
  };
}

interface ReportSchedule {
  id: string;
  report_id: string;
  user_id: string;
  schedule_name: string;
  frequency: string;
  day_of_week?: number;
  day_of_month?: number;
  hour: number;
  minute: number;
  timezone: string;
  is_active: boolean;
  last_run_at?: string;
  next_run_at?: string;
  created_at: string;
  updated_at: string;
  report?: Report;
}

interface ReportMetric {
  id: string;
  name: string;
  description: string;
  metric_type: string;
  calculation_method: string;
  unit: string;
  data_source: string;
  is_active: boolean;
}

interface ReportDashboard {
  id: string;
  user_id: string;
  name: string;
  description: string;
  layout_config: any;
  widgets: any;
  is_public: boolean;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

interface ReportsSummary {
  total_reports: number;
  completed_reports: number;
  generating_reports: number;
  scheduled_reports: number;
  recent_reports: Report[];
  reports_by_type: Array<{ type: string; count: number }>;
}

interface Fund {
  id: string;
  name: string;
  legal_name: string;
  lei: string;
  isin: string;
  fund_type: string;
  domicile: string;
  status: string;
  risk_level: string;
  total_assets: number;
}

const ReportsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  // Data states
  const [funds, setFunds] = useState<Fund[]>([]);
  const [selectedFundId, setSelectedFundId] = useState<string>('');
  const [summary, setSummary] = useState<ReportsSummary | null>(null);
  const [templates, setTemplates] = useState<ReportTemplate[]>([]);
  const [reports, setReports] = useState<Report[]>([]);
  const [schedules, setSchedules] = useState<ReportSchedule[]>([]);
  const [metrics, setMetrics] = useState<ReportMetric[]>([]);
  const [dashboards, setDashboards] = useState<ReportDashboard[]>([]);

  // Dialog states
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<ReportTemplate | null>(null);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);

  // Filter states
  const [reportTypeFilter, setReportTypeFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');

  useEffect(() => {
    loadReportsData();
  }, []);

  const loadReportsData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load all data in parallel
      const [
        summaryRes,
        templatesRes,
        reportsRes,
        schedulesRes,
        // categoriesRes, // Categories functionality not yet implemented
        metricsRes,
        dashboardsRes,
        fundsRes
      ] = await Promise.all([
        api.get('/reports/summary'),
        api.get('/reports/templates'),
        api.get('/reports/'),
        api.get('/reports/schedules'),
        api.get('/reports/categories'),
        api.get('/reports/metrics'),
        api.get('/reports/dashboards'),
        api.get('/funds/')
      ]);

      setSummary(summaryRes.data);
      setTemplates(templatesRes.data);
      // Backend returns list directly, not wrapped in 'reports' key
      const reportsData = Array.isArray(reportsRes.data) ? reportsRes.data : [];
      LoggerService.debug('Reports loaded:', reportsData.length, reportsData);
      setReports(reportsData);
      setSchedules(schedulesRes.data);
      // setCategories(categoriesRes.data); // Categories functionality not yet implemented
      setMetrics(metricsRes.data);
      setDashboards(dashboardsRes.data);
      setFunds(fundsRes.data?.funds || fundsRes.data || []); // Handle both object with 'funds' key and direct array

    } catch (error: any) {
      LoggerService.error('Error loading reports data:', error);
      setError('Failed to load reports data');
      enqueueSnackbar('Failed to load reports data', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = async (templateId: string, fundId?: string) => {
    try {
      await api.post('/reports/generate', {
        template_id: templateId,
        fund_id: fundId,
        parameters: {}
      });

      enqueueSnackbar('Report generation started', { variant: 'success' });
      setTemplateDialogOpen(false);
      
      // Refresh reports list
      setTimeout(() => {
        loadReportsData();
      }, 2000);

    } catch (error: any) {
      LoggerService.error('Error generating report:', error);
      enqueueSnackbar('Failed to generate report', { variant: 'error' });
    }
  };

  const handleDownloadReport = async (reportId: string) => {
    try {
      const response = await api.get(`/reports/${reportId}/download`, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Extract filename from content-disposition header or use default
      const disposition = response.headers['content-disposition'];
      const match = disposition && disposition.match(/filename="?([^"]+)"?/);
      link.download = match ? match[1] : `report-${reportId}.pdf`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      enqueueSnackbar('Report downloaded', { variant: 'success' });
    } catch (error: any) {
      LoggerService.error('Error downloading report:', error);
      enqueueSnackbar('Failed to download report', { variant: 'error' });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'generating': return 'warning';
      case 'failed': return 'error';
      case 'draft': return 'default';
      default: return 'default';
    }
  };

  const getReportTypeColor = (type: string) => {
    switch (type) {
      case 'Fund Performance': return 'primary';
      case 'Risk Analysis': return 'error';
      case 'Compliance': return 'success';
      case 'Regulatory': return 'info';
      default: return 'default';
    }
  };

  const filteredReports = reports.filter(report => {
    const matchesType = !reportTypeFilter || report.report_type === reportTypeFilter;
    const matchesStatus = !statusFilter || report.status === statusFilter;
    const matchesSearch = !searchTerm || 
      report.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesType && matchesStatus && matchesSearch;
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
          Reports
        </Typography>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
        Reports
      </Typography>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Description color="primary" sx={{ mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Reports
                  </Typography>
                  <Typography variant="h4">
                    {summary?.total_reports || 0}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Assessment color="success" sx={{ mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Completed
                  </Typography>
                  <Typography variant="h4">
                    {summary?.completed_reports || 0}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Schedule color="warning" sx={{ mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Scheduled
                  </Typography>
                  <Typography variant="h4">
                    {summary?.scheduled_reports || 0}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <TrendingUp color="info" sx={{ mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Generating
                  </Typography>
                  <Typography variant="h4">
                    {summary?.generating_reports || 0}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content */}
      <Paper sx={{ p: 3 }}>
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
          <Tab label="Reports" icon={<Description />} />
          <Tab label="Templates" icon={<Assessment />} />
          <Tab label="Schedules" icon={<Schedule />} />
          <Tab label="Dashboards" icon={<DashboardIcon />} />
          <Tab label="Metrics" icon={<BarChart />} />
        </Tabs>

        {/* Reports Tab */}
        {activeTab === 0 && (
          <Box>
            {/* Filters */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  placeholder="Search reports..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Report Type</InputLabel>
                  <Select
                    value={reportTypeFilter}
                    onChange={(e) => setReportTypeFilter(e.target.value)}
                    label="Report Type"
                  >
                    <MenuItem value="">All Types</MenuItem>
                    <MenuItem value="Fund Performance">Fund Performance</MenuItem>
                    <MenuItem value="Risk Analysis">Risk Analysis</MenuItem>
                    <MenuItem value="Compliance">Compliance</MenuItem>
                    <MenuItem value="Regulatory">Regulatory</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="">All Statuses</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="generating">Generating</MenuItem>
                    <MenuItem value="draft">Draft</MenuItem>
                    <MenuItem value="failed">Failed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => setTemplateDialogOpen(true)}
                  fullWidth
                >
                  Generate Report
                </Button>
              </Grid>
            </Grid>

            {/* Reports Table */}
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Title</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Fund</TableCell>
                    <TableCell>Generated</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredReports.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          {reports.length === 0 
                            ? 'No reports found. Generate a report to get started.'
                            : 'No reports match the current filters.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredReports.map((report) => (
                      <TableRow key={report.id}>
                        <TableCell>
                          <Box>
                            <Typography variant="subtitle2">{report.title}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {report.description}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={report.report_type}
                            color={getReportTypeColor(report.report_type) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={report.status}
                            color={getStatusColor(report.status) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {(() => {
                            const fund = funds.find(f => f.id === report.fund_id);
                            return fund?.name || 'All Funds';
                          })()}
                        </TableCell>
                        <TableCell>
                          {report.generation_completed_at 
                            ? new Date(report.generation_completed_at).toLocaleDateString()
                            : 'N/A'
                          }
                        </TableCell>
                        <TableCell>
                          <Box display="flex" gap={1}>
                            {report.status === 'completed' && (
                              <Tooltip title="Download">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDownloadReport(report.id)}
                                >
                                  <Download />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSelectedReport(report);
                                  setReportDialogOpen(true);
                                }}
                              >
                                <Visibility />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Templates Tab */}
        {activeTab === 1 && (
          <Grid container spacing={3}>
            {templates.map((template) => (
              <Grid item xs={12} md={6} lg={4} key={template.id}>
                <Card>
                  <CardHeader
                    title={template.name}
                    subheader={template.category}
                    action={
                      <Chip
                        label={template.report_type}
                        color="primary"
                        size="small"
                      />
                    }
                  />
                  <CardContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {template.description}
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<Add />}
                      onClick={() => {
                        setSelectedTemplate(template);
                        setTemplateDialogOpen(true);
                      }}
                      fullWidth
                    >
                      Generate Report
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Schedules Tab */}
        {activeTab === 2 && (
          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
              <Typography variant="h6">Scheduled Reports</Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => LoggerService.debug('Schedule dialog - not yet implemented')}
              >
                Create Schedule
              </Button>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Schedule Name</TableCell>
                    <TableCell>Report</TableCell>
                    <TableCell>Frequency</TableCell>
                    <TableCell>Next Run</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {schedules.map((schedule) => (
                    <TableRow key={schedule.id}>
                      <TableCell>{schedule.schedule_name}</TableCell>
                      <TableCell>{schedule.report?.title}</TableCell>
                      <TableCell>
                        <Chip label={schedule.frequency} size="small" />
                      </TableCell>
                      <TableCell>
                        {schedule.next_run_at 
                          ? new Date(schedule.next_run_at).toLocaleString()
                          : 'N/A'
                        }
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={schedule.is_active ? 'Active' : 'Inactive'}
                          color={schedule.is_active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Dashboards Tab */}
        {activeTab === 3 && (
          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
              <Typography variant="h6">Report Dashboards</Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => LoggerService.debug('Dashboard dialog - not yet implemented')}
              >
                Create Dashboard
              </Button>
            </Box>
            <Grid container spacing={3}>
              {dashboards.map((dashboard) => (
                <Grid item xs={12} md={6} lg={4} key={dashboard.id}>
                  <Card>
                    <CardHeader
                      title={dashboard.name}
                      subheader={dashboard.is_public ? 'Public' : 'Private'}
                    />
                    <CardContent>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {dashboard.description}
                      </Typography>
                      <Box display="flex" gap={1}>
                        <Button size="small" startIcon={<Visibility />}>
                          View
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Metrics Tab */}
        {activeTab === 4 && (
          <Box>
            <Typography variant="h6" sx={{ mb: 3 }}>Available Metrics</Typography>
            <Grid container spacing={3}>
              {metrics.map((metric) => (
                <Grid item xs={12} md={6} lg={4} key={metric.id}>
                  <Card>
                    <CardHeader
                      title={metric.name}
                      subheader={metric.metric_type}
                      action={
                        <Chip label={metric.unit} size="small" />
                      }
                    />
                    <CardContent>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {metric.description}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Data Source: {metric.data_source}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Paper>

      {/* Generate Report Dialog */}
      <Dialog open={templateDialogOpen} onClose={() => setTemplateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Generate Report</DialogTitle>
        <DialogContent>
          {selectedTemplate && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>{selectedTemplate.name}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {selectedTemplate.description}
              </Typography>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Fund (Optional)</InputLabel>
                <Select
                  value={selectedFundId}
                  onChange={(e) => setSelectedFundId(e.target.value)}
                  label="Fund (Optional)"
                >
                  <MenuItem value="">All Funds</MenuItem>
                  {funds.map((fund) => (
                    <MenuItem key={fund.id} value={fund.id}>
                      {fund.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => selectedTemplate && handleGenerateReport(selectedTemplate.id, selectedFundId || undefined)}
          >
            Generate Report
          </Button>
        </DialogActions>
      </Dialog>

      {/* Report Details Dialog */}
      <Dialog open={reportDialogOpen} onClose={() => setReportDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Report Details</DialogTitle>
        <DialogContent>
          {selectedReport && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>{selectedReport.title}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {selectedReport.description}
              </Typography>
              
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">Status</Typography>
                  <Chip
                    label={selectedReport.status}
                    color={getStatusColor(selectedReport.status) as any}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">Type</Typography>
                  <Chip
                    label={selectedReport.report_type}
                    color={getReportTypeColor(selectedReport.report_type) as any}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">Generated</Typography>
                  <Typography variant="body2">
                    {selectedReport.generation_completed_at 
                      ? new Date(selectedReport.generation_completed_at).toLocaleString()
                      : 'Not generated'
                    }
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">File Size</Typography>
                  <Typography variant="body2">
                    {selectedReport.file_size ? `${(selectedReport.file_size / 1024).toFixed(1)} KB` : 'N/A'}
                  </Typography>
                </Grid>
              </Grid>

              {selectedReport.report_data && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 2 }}>Report Data</Typography>
                  <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                    <pre style={{ fontSize: '12px', overflow: 'auto' }}>
                      {JSON.stringify(selectedReport.report_data, null, 2)}
                    </pre>
                  </Paper>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportDialogOpen(false)}>Close</Button>
          {selectedReport?.status === 'completed' && (
            <Button
              variant="contained"
              startIcon={<Download />}
              onClick={() => selectedReport && handleDownloadReport(selectedReport.id)}
            >
              Download
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="generate report"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={() => setTemplateDialogOpen(true)}
      >
        <Add />
      </Fab>
    </Box>
  );
};

export default ReportsPage;
